import { useMemo, useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import AccountSelect from '../../components/AccountSelect';
import { reportsApi, type CashBookDto } from '../../api/reports';
import MoneyCell from '../../components/MoneyCell';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useReportScreen } from '../../components/ScreenContext';
import { formatMoney } from '../../utils/formatMoney';

/**
 * KASOS KNYGA — legacy R_TillBook layout: opening balance, one row per
 * posted cash document (income = debit side of the till account), period
 * totals, closing balance. No per-row running balance by design.
 */
export default function CashBook() {
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [account, setAccount] = useState<number | null>(null);
  const [data, setData] = useState<CashBookDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!account) {
      setError('Pasirinkite kasos sąskaitą.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      setData(await reportsApi.cashBook({ from, to, account }));
    } catch (e: any) {
      setError(e.message || 'Nepavyko sugeneruoti kasos knygos');
    } finally {
      setLoading(false);
    }
  };

  const handleCsv = async () => {
    if (!account) return;
    try {
      await reportsApi.cashBookCsv({ from, to, account });
    } catch (e: any) {
      setError(e.message || 'Eksportas nepavyko');
    }
  };

  const recordCount = data?.rows.length ?? 0;
  const screenPreview = useMemo(
    () =>
      (data?.rows ?? []).slice(0, 10).map(r =>
        `${r.date} ${r.documentNumber}: ${r.income != null ? `gauta ${formatMoney(r.income)}` : `ismoketa ${formatMoney(r.expense ?? 0)}`}`.substring(
          0,
          120,
        ),
      ),
    [data],
  );

  useReportScreen(
    data
      ? `Kasos knyga (${data.account}, ${data.from}–${data.to}): pradžia ${formatMoney(data.openingBalance)}, gauta ${formatMoney(data.totalIncome)}, išmokėta ${formatMoney(data.totalExpense)}, pabaiga ${formatMoney(data.closingBalance)}`
      : 'Kasos knyga: nesugeneruota',
    {
      recordCount,
      filters: { nuo: from, iki: to, ...(account != null && { saskaita: String(account) }) },
      preview: screenPreview,
    },
  );

  return (
    <div className="space-y-4">
      <ReportsNav />
      <h1 className="print:hidden">Kasos knyga</h1>

      <div
        className="filters print:hidden"
        style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}
      >
        <label>
          Nuo: <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label>
          Iki: <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <label>
          Kasos sąskaita: <AccountSelect value={account} onChange={setAccount} />
        </label>
        <button onClick={handleGenerate} disabled={loading || !account}>
          {loading ? <LoadingSpinner /> : 'Rodyti'}
        </button>
        {data && (
          <button onClick={handleCsv} className="secondary">
            CSV
          </button>
        )}
      </div>

      {error && <div className="error-message print:hidden">{error}</div>}

      {data && (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Dok. Nr.</th>
                <th>Gauta iš / išmokėta kam</th>
                <th>Turinys</th>
                <th style={{ textAlign: 'right' }}>Pajamos</th>
                <th style={{ textAlign: 'right' }}>Išlaidos</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={4}>
                  <em>Likutis laikotarpio pradžioje:</em>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <em>
                    <MoneyCell value={data.openingBalance} />
                  </em>
                </td>
                <td></td>
              </tr>
              {data.rows.map((r, i) => (
                <tr key={i}>
                  <td>{r.date}</td>
                  <td>{r.documentNumber}</td>
                  <td>{r.personName ?? ''}</td>
                  <td>{r.content}</td>
                  <td style={{ textAlign: 'right' }}>
                    {r.income != null ? <MoneyCell value={r.income} /> : ''}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {r.expense != null ? <MoneyCell value={r.expense} /> : ''}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr>
                  <td colSpan={6}>Periode kasos operacijų nėra</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan={4}>
                  <strong>Apyvarta per laikotarpį:</strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.totalIncome} />
                  </strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.totalExpense} />
                  </strong>
                </td>
              </tr>
              <tr className="total-row">
                <td colSpan={4}>
                  <strong>Likutis laikotarpio pabaigoje:</strong>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <strong>
                    <MoneyCell value={data.closingBalance} />
                  </strong>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
